export default {
  props: {
    offers: {
      type: Array,
      default: () => [],
    },
    mainOffers: {
      type: Array,
      default: () => [],
    },
    sortingOffers: {
      type: Array,
      default: () => [],
    },
    reserveOffers: {
      type: Array,
      default: () => [],
    },
    activePrimaryBucket: {
      type: String,
      default: "main",
    },
    checkerOffers: {
      type: Array,
      default: () => [],
    },
    checkerOffersPlacement: {
      type: String,
      default: "before",
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    displayedOffers: [],
    reservePool: [],
    nextReplaceIndex: 0,
    replaceTimerId: null,
    isReplacementShown: false,
  }),

  computed: {
    normalizedCheckerOffersPlacement() {
      const placement = String(this.checkerOffersPlacement || "").toLowerCase();

      return ["before", "after", "inline"].includes(placement) ? placement : "before";
    },

    shouldRenderCheckerOffersInline() {
      return this.normalizedCheckerOffersPlacement === "inline";
    },

    shouldRenderCheckerOffersBefore() {
      return this.normalizedCheckerOffersPlacement === "before";
    },

    checkerOfferKeys() {
      return new Set((this.checkerOffers || []).map(this.getOfferKey).filter(Boolean));
    },

    filteredOffers() {
      return this.displayedOffers || [];
    },

    filteredOffersLength() {
      return this.filteredOffers.length;
    },

    mainOffersSource() {
      return (this.mainOffers && this.mainOffers.length ? this.mainOffers : this.offers) || [];
    },

    sortingOffersSource() {
      return this.sortingOffers || [];
    },

    filteredPrimaryOffersSource() {
      const mainOffers = this.excludeCheckerOffers(this.mainOffersSource);

      if (mainOffers.length > 0) {
        return mainOffers;
      }

      return this.excludeCheckerOffers(this.offers || this.sortingOffersSource);
    },

    filteredReplacementOffersSource() {
      const primaryOfferKeys = new Set(this.filteredPrimaryOffersSource.map(this.getOfferKey).filter(Boolean));
      const replacementOffers = [
        ...(this.sortingOffersSource || []),
        ...(this.reserveOffers || []),
      ];

      return this.excludeCheckerOffers(replacementOffers).filter((offer) => {
        const key = this.getOfferKey(offer);

        if (!key) return true;

        return !primaryOfferKeys.has(key);
      });
    },

    replaceTimerSeconds() {
      const value = Number(this.settings?.offers_replace_timer);

      if (!Number.isFinite(value) || value <= 0) {
        return 0;
      }

      return value;
    },
  },

  watch: {
    filteredPrimaryOffersSource: {
      handler() {
        this.syncOfferPools();
      },
      immediate: true,
    },

    filteredReplacementOffersSource() {
      this.syncOfferPools();
    },

    replaceTimerSeconds() {
      this.restartOfferRotation();
    },
  },

  beforeUnmount() {
    this.stopOfferRotation();
  },

  methods: {
    buildOrderedCheckerOffers(primaryOffersSource) {
      const orderedOffers = Array.isArray(this.offers) ? this.offers : [];
      const primaryOffers = Array.isArray(primaryOffersSource) && primaryOffersSource.length
        ? primaryOffersSource
        : (
          Array.isArray(this.mainOffers) && this.mainOffers.length
            ? this.mainOffers
            : orderedOffers
        );
      const checkerOffers = Array.isArray(this.checkerOffers) ? this.checkerOffers : [];

      if (checkerOffers.length === 0) {
        return primaryOffers;
      }

      if (orderedOffers.length === 0) {
        const checkerKeys = new Set(
          checkerOffers.map((offer) => this.getOfferKey(offer)).filter(Boolean)
        );

        const primaryWithoutChecker = primaryOffers.filter((offer) => {
          const key = this.getOfferKey(offer);

          if (!key) {
            return true;
          }

          return !checkerKeys.has(key);
        });

        return [...checkerOffers, ...primaryWithoutChecker];
      }

      const allowedKeys = new Set(
        [...primaryOffers, ...checkerOffers].map((offer) => this.getOfferKey(offer)).filter(Boolean)
      );

      const mergedOffers = [];
      const seenKeys = new Set();

      orderedOffers.forEach((offer) => {
        const key = this.getOfferKey(offer);

        if (!key || !allowedKeys.has(key) || seenKeys.has(key)) {
          return;
        }

        mergedOffers.push(offer);
        seenKeys.add(key);
      });

      [...checkerOffers, ...primaryOffers].forEach((offer) => {
        const key = this.getOfferKey(offer);

        if (!key) {
          mergedOffers.push(offer);
          return;
        }

        if (seenKeys.has(key)) {
          return;
        }

        mergedOffers.push(offer);
        seenKeys.add(key);
      });

      return mergedOffers;
    },

    getInlineOrderedOffers(primaryOffersSource) {
      const orderedOffers = Array.isArray(this.offers) ? this.offers : [];

      if (orderedOffers.length > 0) {
        return orderedOffers;
      }

      return this.buildOrderedCheckerOffers(primaryOffersSource);
    },

    getCheckerDisplayOffers() {
      if (!this.shouldRenderCheckerOffersBefore) {
        return [];
      }

      return Array.isArray(this.checkerOffers) ? this.checkerOffers : [];
    },

    getInlineShowcaseOffers(offers = this.filteredOffers) {
      const list = Array.isArray(offers) ? offers : [];
      const checkerOffers = Array.isArray(this.checkerOffers) ? this.checkerOffers : [];

      if (this.shouldRenderCheckerOffersInline || checkerOffers.length === 0) {
        return list;
      }

      return list.filter((offer) => {
        const key = this.getOfferKey(offer);

        if (!key) return true;

        return !this.checkerOfferKeys.has(key);
      });
    },

    excludeCheckerOffers(offers) {
      if (!this.checkerOffers || this.checkerOffers.length === 0) {
        return offers || [];
      }

      return (offers || []).filter((offer) => {
        const key = this.getOfferKey(offer);

        if (!key) return true;

        return !this.checkerOfferKeys.has(key);
      });
    },

    getOfferKey(offer) {
      if (!offer) return null;

      if (offer.link) {
        return `link:${offer.link}`;
      }

      if (offer.id) {
        return `id:${offer.id}`;
      }

      if (offer.name) {
        return `name:${offer.name}|amount:${offer.amount}|period:${offer.period}`;
      }

      return null;
    },

    syncOfferPools() {
      const primaryOffers = [...this.filteredPrimaryOffersSource];
      const replacementOffers = [...this.filteredReplacementOffersSource];

      this.displayedOffers = primaryOffers;
      this.reservePool = replacementOffers;
      this.nextReplaceIndex = 0;
      this.isReplacementShown = false;

      this.restartOfferRotation();
    },

    restartOfferRotation() {
      this.stopOfferRotation();

      if (
        this.replaceTimerSeconds <= 0 ||
        this.displayedOffers.length === 0 ||
        this.reservePool.length === 0
      ) {
        return;
      }

      this.replaceTimerId = window.setInterval(() => {
        this.rotateOffersFromReserve();
      }, this.replaceTimerSeconds * 1000);
    },

    stopOfferRotation() {
      if (!this.replaceTimerId) return;

      window.clearInterval(this.replaceTimerId);
      this.replaceTimerId = null;
    },

    rotateOffersFromReserve() {
      if (this.filteredPrimaryOffersSource.length === 0) {
        if (this.reservePool.length > 0) {
          this.displayedOffers = [this.reservePool[0]];
        }
        this.stopOfferRotation();
        return;
      }

      if (this.reservePool.length === 0) {
        this.displayedOffers = [...this.filteredPrimaryOffersSource];
        this.isReplacementShown = false;
        this.stopOfferRotation();
        return;
      }

      const primaryOffers = [...this.filteredPrimaryOffersSource];

      if (!this.isReplacementShown) {
        const replacementOffer = this.reservePool[this.nextReplaceIndex % this.reservePool.length];
        this.displayedOffers = replacementOffer ? [replacementOffer] : [];
        this.isReplacementShown = true;
        return;
      }

      this.displayedOffers = primaryOffers;
      this.isReplacementShown = false;
      this.nextReplaceIndex = (this.nextReplaceIndex + 1) % Math.max(this.reservePool.length, 1);
    },
  },
};
